import React, { useMemo } from "react";
import { SensorKitAlert } from "@/alerts/alert";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { AlertTimeline } from "./AlertTimeline";
import { useAlertLogsInfinite } from "@/alerts/api";

interface AlertCardProps {
  alert: SensorKitAlert;
  isExpanded: boolean;
  onToggle: () => void;
}

// Helper function to check if alert_type indicates firing
const isFiringAlert = (alertType: string): boolean => {
  return alertType.includes('_to_firing') || alertType === 'firing';
};

export const AlertCard = ({ alert, isExpanded, onToggle }: AlertCardProps) => {
  // Fetch alert logs to find the first firing time
  const { data: alertLogsData } = useAlertLogsInfinite({ alert_id: alert.id });

  // Find the first time the alert fired
  const firstFiringTime = useMemo(() => {
    if (!alertLogsData?.pages) return null;
    
    const allLogs = alertLogsData.pages.flatMap(page => page.results) || [];
    
    // Sort chronologically (oldest first) to find the first firing
    const chronologicalLogs = [...allLogs].sort((a, b) => 
      new Date(a.time).getTime() - new Date(b.time).getTime()
    );
    
    // Find the first log entry where the alert fired
    const firstFiringLog = chronologicalLogs.find(log => isFiringAlert(log.alert_type));
    
    return firstFiringLog ? new Date(firstFiringLog.time) : null;
  }, [alertLogsData]);

  // Use first firing time if available, otherwise fall back to first_message_received
  const firstSeenTime = firstFiringTime || new Date(alert.first_message_received);

  return (
    <Card className="mb-2">
      <Collapsible open={isExpanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-4 h-auto text-left">
            <div className="flex flex-col items-start space-y-1">
              <span className="font-medium">{alert.alert_reason}</span>
              <div className="flex space-x-4 text-sm text-gray-600">
                <span>First seen: {firstSeenTime.toLocaleString()}</span>
                <span>Last seen: {new Date(alert.last_message_received).toLocaleString()}</span>
              </div>
            </div>
            {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-gray-200 pt-3">
            <AlertTimeline alertId={alert.id} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
